import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Building2,
  Calendar,
  ChevronLeft,
  ChevronRight,
  ExternalLink,
  Landmark,
  MapPin,
  Plus,
  Star,
  Users,
} from 'lucide-react';
import {
  createAdminJob,
  fetchAdminJob,
  fetchAdminJobs,
  patchAdminOpportunity,
  type AdminOpportunityRow,
} from '../../api/admin';
import { ApiError } from '../../api/client';
import {
  AdminDetailCard,
  AdminEmptyState,
  AdminKpiStrip,
  AdminPageLayout,
  AdminSearchInput,
  AdminStatusBadge,
  AdminTablePanel,
} from '../../components/admin/AdminPageLayout';
import { LoadingState, ErrorState } from '../../components/states/StateViews';

function CreateJobModal({
  open,
  onClose,
  qualifications,
}: {
  open: boolean;
  onClose: () => void;
  qualifications: string[];
}) {
  const qc = useQueryClient();
  const [form, setForm] = useState({
    title: '',
    organization: '',
    department: '',
    qualification: qualifications[0] ?? 'graduate',
    state: 'All India',
    vacancies: '',
    salary: '',
    applicationStart: '',
    applicationEnd: '',
    status: 'open',
    isFeatured: false,
    officialApplyUrl: '',
  });
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open) return;
    setForm({
      title: '',
      organization: '',
      department: '',
      qualification: qualifications[0] ?? 'graduate',
      state: 'All India',
      vacancies: '',
      salary: '',
      applicationStart: '',
      applicationEnd: '',
      status: 'open',
      isFeatured: false,
      officialApplyUrl: '',
    });
    setError('');
  }, [open, qualifications]);

  const create = useMutation({
    mutationFn: () =>
      createAdminJob({
        title: form.title.trim(),
        organization: form.organization.trim(),
        department: form.department.trim() || undefined,
        qualification: form.qualification,
        state: form.state,
        vacancies: form.vacancies ? Number(form.vacancies) : null,
        salary: form.salary.trim() || undefined,
        applicationStart: form.applicationStart || null,
        applicationEnd: form.applicationEnd || null,
        status: form.status,
        isFeatured: form.isFeatured,
        officialApplyUrl: form.officialApplyUrl.trim() || undefined,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['admin-jobs'] });
      onClose();
    },
    onError: (err) => {
      setError(err instanceof ApiError ? err.message : 'Failed to create job listing');
    },
  });

  if (!open) return null;

  return (
    <div className="wfx-admin-modal-backdrop" role="presentation" onClick={onClose}>
      <div
        className="wfx-admin-modal glass-surface"
        role="dialog"
        aria-labelledby="create-job-title"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="wfx-admin-modal-head">
          <div>
            <p className="wfx-admin-breadcrumb">Government jobs</p>
            <h2 id="create-job-title"><Landmark size={20} /> New job listing</h2>
          </div>
        </header>
        <form
          className="wfx-admin-form"
          onSubmit={(e) => {
            e.preventDefault();
            setError('');
            create.mutate();
          }}
        >
          <label className="wfx-admin-field">
            <span>Job title</span>
            <input required value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} />
          </label>
          <label className="wfx-admin-field">
            <span>Organization / recruiting body</span>
            <input required value={form.organization} onChange={(e) => setForm((f) => ({ ...f, organization: e.target.value }))} />
          </label>
          <div className="wfx-admin-form-row">
            <label className="wfx-admin-field">
              <span>Department</span>
              <input value={form.department} onChange={(e) => setForm((f) => ({ ...f, department: e.target.value }))} />
            </label>
            <label className="wfx-admin-field">
              <span>Vacancies</span>
              <input type="number" value={form.vacancies} onChange={(e) => setForm((f) => ({ ...f, vacancies: e.target.value }))} />
            </label>
          </div>
          <div className="wfx-admin-form-row">
            <label className="wfx-admin-field">
              <span>Qualification</span>
              <select value={form.qualification} onChange={(e) => setForm((f) => ({ ...f, qualification: e.target.value }))}>
                {qualifications.map((q) => (
                  <option key={q} value={q}>{q}</option>
                ))}
              </select>
            </label>
            <label className="wfx-admin-field">
              <span>State / region</span>
              <input value={form.state} onChange={(e) => setForm((f) => ({ ...f, state: e.target.value }))} />
            </label>
          </div>
          <label className="wfx-admin-field">
            <span>Salary / pay scale</span>
            <input value={form.salary} placeholder="₹25,500 – ₹81,100" onChange={(e) => setForm((f) => ({ ...f, salary: e.target.value }))} />
          </label>
          <div className="wfx-admin-form-row">
            <label className="wfx-admin-field">
              <span>Application start</span>
              <input type="date" value={form.applicationStart} onChange={(e) => setForm((f) => ({ ...f, applicationStart: e.target.value }))} />
            </label>
            <label className="wfx-admin-field">
              <span>Application end</span>
              <input type="date" value={form.applicationEnd} onChange={(e) => setForm((f) => ({ ...f, applicationEnd: e.target.value }))} />
            </label>
          </div>
          <label className="wfx-admin-field">
            <span>Apply URL</span>
            <input value={form.officialApplyUrl} placeholder="https://" onChange={(e) => setForm((f) => ({ ...f, officialApplyUrl: e.target.value }))} />
          </label>
          <label className="wfx-admin-field">
            <span>Status</span>
            <select value={form.status} onChange={(e) => setForm((f) => ({ ...f, status: e.target.value }))}>
              <option value="open">Open</option>
              <option value="closing_soon">Closing soon</option>
              <option value="closed">Closed</option>
            </select>
          </label>
          <label className="wfx-admin-checkbox">
            <input type="checkbox" checked={form.isFeatured} onChange={(e) => setForm((f) => ({ ...f, isFeatured: e.target.checked }))} />
            Feature on homepage
          </label>
          {error && <p className="wfx-admin-form-error">{error}</p>}
          <div className="wfx-admin-modal-actions">
            <button type="button" className="secondary-action" onClick={onClose}>Cancel</button>
            <button type="submit" className="primary-action" disabled={create.isPending}>
              {create.isPending ? 'Creating…' : 'Publish job'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function JobDetailCard({
  jobId,
  qualifications,
  onClose,
}: {
  jobId: string | null;
  qualifications: string[];
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['admin-job', jobId],
    queryFn: () => fetchAdminJob(jobId!),
    enabled: Boolean(jobId),
  });

  const update = useMutation({
    mutationFn: (body: Partial<AdminOpportunityRow>) => patchAdminOpportunity(jobId!, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['admin-jobs'] });
      void qc.invalidateQueries({ queryKey: ['admin-job', jobId] });
      void qc.invalidateQueries({ queryKey: ['admin-opportunities'] });
    },
  });

  const row = data?.job;

  return (
    <AdminDetailCard
      open={Boolean(jobId)}
      onClose={onClose}
      ariaLabel="Job listing details"
      breadcrumb="Government job"
      title={row?.title ?? 'Loading…'}
      illustration="card.jobs"
      subtitle={
        row ? (
          <>
            <AdminStatusBadge status={row.status} />
            {row.isFeatured && (
              <span className="wfx-admin-opp-flag">
                <Star size={14} aria-hidden="true" /> Featured
              </span>
            )}
            <span className="wfx-muted wfx-admin-subline">{row.organization}</span>
          </>
        ) : undefined
      }
    >
      {isLoading && <LoadingState label="Loading job…" />}
      {isError && <ErrorState message="Could not load job listing." onRetry={() => void refetch()} />}

      {row && (
        <>
          <dl className="wfx-admin-meta-list wfx-admin-meta-list--grid">
            <div><dt>Department</dt><dd>{row.department ?? '—'}</dd></div>
            <div><dt>Vacancies</dt><dd>{row.vacancies ?? '—'}</dd></div>
            <div><dt>State</dt><dd>{row.state ?? '—'}</dd></div>
            <div><dt>Salary</dt><dd>{row.salary ?? '—'}</dd></div>
            <div><dt>Qualification</dt><dd>{row.qualification}</dd></div>
            <div>
              <dt>Public page</dt>
              <dd>
                <Link to={`/opportunities/${row.slug}`} className="secondary-action compact" target="_blank">
                  View listing
                  <ExternalLink size={12} aria-hidden="true" />
                </Link>
              </dd>
            </div>
          </dl>

          <div className="wfx-admin-drawer-columns">
            <div className="wfx-admin-drawer-section">
              <h3>Job details</h3>
              <div className="wfx-admin-form">
                <label className="wfx-admin-field">
                  <span>Title</span>
                  <input
                    defaultValue={row.title}
                    onBlur={(e) => {
                      const value = e.target.value.trim();
                      if (value && value !== row.title) update.mutate({ title: value });
                    }}
                  />
                </label>
                <label className="wfx-admin-field">
                  <span>Organization</span>
                  <input
                    defaultValue={row.organization}
                    onBlur={(e) => {
                      const value = e.target.value.trim();
                      if (value && value !== row.organization) update.mutate({ organization: value });
                    }}
                  />
                </label>
                <div className="wfx-admin-form-row">
                  <label className="wfx-admin-field">
                    <span>Department</span>
                    <input
                      defaultValue={row.department ?? ''}
                      onBlur={(e) => {
                        const value = e.target.value.trim();
                        if (value !== (row.department ?? '')) update.mutate({ department: value || null });
                      }}
                    />
                  </label>
                  <label className="wfx-admin-field">
                    <span>Vacancies</span>
                    <input
                      type="number"
                      defaultValue={row.vacancies ?? ''}
                      onBlur={(e) => {
                        const value = e.target.value ? Number(e.target.value) : null;
                        if (value !== row.vacancies) update.mutate({ vacancies: value });
                      }}
                    />
                  </label>
                </div>
                <div className="wfx-admin-form-row">
                  <label className="wfx-admin-field">
                    <span>Qualification</span>
                    <select
                      defaultValue={row.qualification}
                      onChange={(e) => update.mutate({ qualification: e.target.value })}
                    >
                      {qualifications.map((q) => (
                        <option key={q} value={q}>{q}</option>
                      ))}
                    </select>
                  </label>
                  <label className="wfx-admin-field">
                    <span>Status</span>
                    <select
                      defaultValue={row.status}
                      onChange={(e) => update.mutate({ status: e.target.value })}
                    >
                      <option value="open">Open</option>
                      <option value="closing_soon">Closing soon</option>
                      <option value="closed">Closed</option>
                    </select>
                  </label>
                </div>
                <label className="wfx-admin-field">
                  <span>Salary</span>
                  <input
                    defaultValue={row.salary ?? ''}
                    onBlur={(e) => {
                      const value = e.target.value.trim();
                      if (value !== (row.salary ?? '')) update.mutate({ salary: value || null });
                    }}
                  />
                </label>
              </div>
            </div>

            <div className="wfx-admin-drawer-section">
              <h3>Location & deadlines</h3>
              <div className="wfx-admin-form">
                <label className="wfx-admin-field">
                  <span>State / region</span>
                  <input
                    defaultValue={row.state ?? ''}
                    onBlur={(e) => {
                      const value = e.target.value.trim();
                      if (value !== (row.state ?? '')) update.mutate({ state: value });
                    }}
                  />
                </label>
                <label className="wfx-admin-field">
                  <span>Location</span>
                  <input
                    defaultValue={row.location ?? ''}
                    onBlur={(e) => {
                      const value = e.target.value.trim();
                      if (value !== (row.location ?? '')) update.mutate({ location: value || null });
                    }}
                  />
                </label>
                <div className="wfx-admin-form-row">
                  <label className="wfx-admin-field">
                    <span>Application start</span>
                    <input
                      type="date"
                      defaultValue={row.applicationStart?.slice(0, 10) ?? ''}
                      onBlur={(e) => {
                        const value = e.target.value;
                        if (value !== (row.applicationStart?.slice(0, 10) ?? '')) {
                          update.mutate({ applicationStart: value || null });
                        }
                      }}
                    />
                  </label>
                  <label className="wfx-admin-field">
                    <span>Application end</span>
                    <input
                      type="date"
                      defaultValue={row.applicationEnd?.slice(0, 10) ?? ''}
                      onBlur={(e) => {
                        const value = e.target.value;
                        if (value !== (row.applicationEnd?.slice(0, 10) ?? '')) {
                          update.mutate({ applicationEnd: value || null });
                        }
                      }}
                    />
                  </label>
                </div>
                <label className="wfx-admin-field">
                  <span>Apply URL</span>
                  <input
                    defaultValue={row.officialApplyUrl ?? ''}
                    placeholder="https://"
                    onBlur={(e) => {
                      const value = e.target.value.trim();
                      if (value !== (row.officialApplyUrl ?? '')) {
                        update.mutate({ officialApplyUrl: value || null });
                      }
                    }}
                  />
                </label>
                <label className="wfx-admin-field">
                  <span>Eligibility</span>
                  <textarea
                    rows={3}
                    className="wfx-admin-textarea"
                    defaultValue={row.eligibility ?? ''}
                    onBlur={(e) => {
                      const value = e.target.value.trim();
                      if (value !== (row.eligibility ?? '')) update.mutate({ eligibility: value || null });
                    }}
                  />
                </label>
                <button
                  type="button"
                  className={`chip-pill${row.isFeatured ? ' is-active' : ''}`}
                  disabled={update.isPending}
                  onClick={() => update.mutate({ isFeatured: !row.isFeatured })}
                >
                  <Star size={14} aria-hidden="true" /> Featured job
                </button>
              </div>
            </div>
          </div>
        </>
      )}
    </AdminDetailCard>
  );
}

function JobCard({
  row,
  onSelect,
}: {
  row: AdminOpportunityRow;
  onSelect: () => void;
}) {
  return (
    <button type="button" className="wfx-admin-job-card glass-surface" onClick={onSelect}>
      <div className="wfx-admin-result-card-head">
        <AdminStatusBadge status={row.status} />
        {row.isFeatured && <Star size={14} aria-hidden="true" className="wfx-admin-job-star" />}
      </div>
      <strong>{row.title}</strong>
      <span className="wfx-muted wfx-admin-result-org">
        <Building2 size={14} aria-hidden="true" /> {row.organization}
      </span>
      <div className="wfx-admin-page-card-stats">
        {row.vacancies != null && (
          <span><Users size={14} aria-hidden="true" /> {row.vacancies} posts</span>
        )}
        {row.state && (
          <span><MapPin size={14} aria-hidden="true" /> {row.state}</span>
        )}
        {row.applicationEnd && (
          <span><Calendar size={14} aria-hidden="true" /> {new Date(row.applicationEnd).toLocaleDateString()}</span>
        )}
      </div>
      {row.salary && <span className="chip-pill">{row.salary}</span>}
    </button>
  );
}

export function AdminJobsScreen() {
  const [query, setQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [stateFilter, setStateFilter] = useState('all');
  const [featuredFilter, setFeaturedFilter] = useState('all');
  const [page, setPage] = useState(1);
  const [createOpen, setCreateOpen] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  useEffect(() => {
    const timer = window.setTimeout(() => setDebouncedQuery(query.trim()), 300);
    return () => window.clearTimeout(timer);
  }, [query]);

  useEffect(() => {
    setPage(1);
  }, [debouncedQuery, statusFilter, stateFilter, featuredFilter]);

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['admin-jobs', debouncedQuery, statusFilter, stateFilter, featuredFilter, page],
    queryFn: () =>
      fetchAdminJobs({
        q: debouncedQuery || undefined,
        status: statusFilter,
        state: stateFilter,
        featured: featuredFilter,
        page,
        limit: 12,
      }),
  });

  const rows = data?.data ?? [];
  const stats = data?.stats;
  const qualifications = data?.qualifications ?? ['graduate'];
  const states = data?.states ?? [];
  const pagination = data?.pagination;

  const kpiItems = useMemo(
    () => [
      { label: 'Total jobs', value: stats?.total ?? 0 },
      { label: 'Open', value: stats?.open ?? 0 },
      { label: 'Total vacancies', value: stats?.vacancies ?? 0 },
      { label: 'States covered', value: stats?.states ?? 0 },
    ],
    [stats],
  );

  if (isLoading && !data) return <LoadingState label="Loading jobs…" />;
  if (isError) return <ErrorState message="Could not load jobs." onRetry={() => void refetch()} />;

  return (
    <>
      <AdminPageLayout
        breadcrumb="Admin / Jobs"
        title="Jobs"
        description="Manage government job listings — vacancies, departments, pay scales, and application deadlines."
        icon={Building2}
        illustration="card.jobs"
        actions={(
          <button type="button" className="primary-action compact" onClick={() => setCreateOpen(true)}>
            <Plus size={16} aria-hidden="true" />
            New job
          </button>
        )}
      >
        <AdminKpiStrip items={kpiItems} />

        <div className="wfx-admin-blog-grid">
          {rows.map((row) => (
            <JobCard key={row.id} row={row} onSelect={() => setSelectedId(row.id)} />
          ))}
        </div>
        {rows.length === 0 && <AdminEmptyState message="No job listings match your filters." />}

        <AdminTablePanel
          title="All government jobs"
          toolbar={(
            <div className="wfx-admin-toolbar">
              <AdminSearchInput
                value={query}
                onChange={setQuery}
                placeholder="Search title, organization, or slug…"
              />
              <select
                className="wfx-admin-select"
                value={stateFilter}
                onChange={(e) => setStateFilter(e.target.value)}
                aria-label="Filter by state"
              >
                <option value="all">All states</option>
                {states.map((state) => (
                  <option key={state} value={state}>{state}</option>
                ))}
              </select>
              <select
                className="wfx-admin-select"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                aria-label="Filter by status"
              >
                <option value="all">All status</option>
                <option value="open">Open</option>
                <option value="closing_soon">Closing soon</option>
                <option value="closed">Closed</option>
              </select>
              <select
                className="wfx-admin-select"
                value={featuredFilter}
                onChange={(e) => setFeaturedFilter(e.target.value)}
                aria-label="Filter by featured"
              >
                <option value="all">All jobs</option>
                <option value="true">Featured only</option>
                <option value="false">Not featured</option>
              </select>
            </div>
          )}
          footer={
            pagination && pagination.pages > 1 ? (
              <div className="wfx-admin-pagination">
                <button
                  type="button"
                  className="secondary-action compact"
                  disabled={page <= 1}
                  onClick={() => setPage((p) => p - 1)}
                >
                  <ChevronLeft size={16} aria-hidden="true" />
                  Previous
                </button>
                <span className="wfx-muted">
                  Page {pagination.page} of {pagination.pages} · {pagination.total} jobs
                </span>
                <button
                  type="button"
                  className="secondary-action compact"
                  disabled={page >= pagination.pages}
                  onClick={() => setPage((p) => p + 1)}
                >
                  Next
                  <ChevronRight size={16} aria-hidden="true" />
                </button>
              </div>
            ) : undefined
          }
        >
          <table className="wfx-admin-table">
            <thead>
              <tr><th>Title</th><th>Organization</th><th>State</th><th>Vacancies</th><th>Status</th><th>Deadline</th></tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr
                  key={row.id}
                  className="wfx-admin-table-row-clickable"
                  onClick={() => setSelectedId(row.id)}
                >
                  <td><strong>{row.title}</strong></td>
                  <td>{row.organization}</td>
                  <td>{row.state ?? '—'}</td>
                  <td>{row.vacancies ?? '—'}</td>
                  <td><AdminStatusBadge status={row.status} /></td>
                  <td>{row.applicationEnd ? new Date(row.applicationEnd).toLocaleDateString() : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </AdminTablePanel>
      </AdminPageLayout>

      <CreateJobModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        qualifications={qualifications}
      />
      <JobDetailCard
        jobId={selectedId}
        qualifications={qualifications}
        onClose={() => setSelectedId(null)}
      />
    </>
  );
}
